<template>
    <component v-model:show="modal" :is="page ? 'div' : 'modal'" body-classes="p-0"
        modal-classes="modal-dialog-centered modal-sm">
        <div class="nch-login" :class="page && 'nch-login--page'">
            <template v-if="!selectUser">
                <header class="nch-login__header">
                    <h2 class="nch-login__title">{{ $t('login.signin') }}</h2>
                    <p class="nch-login__subtitle">
                        ou
                        <a href="#" @click.prevent="$emit('openRegister')">{{ $t('login.new_account') }}</a>
                    </p>
                </header>

                <button v-if="googleEnabled" type="button" class="nch-login__google" @click="socialLogin('Google-plus')">
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    <span>Google</span>
                </button>

                <div v-if="googleEnabled" class="nch-login__divider">
                    <span>{{ $t('login.email') }}</span>
                </div>
            </template>

            <form role="form" novalidate @submit.prevent="login">
                <template v-if="!selectUser">
                    <div v-if="documents.length" class="nch-login__field">
                        <label for="document_type">{{ $t('login.login_type') }}</label>
                        <select id="document_type" name="document_type" class="form-control"
                            v-model="model.document_type">
                            <option value="EMAIL" selected>{{ $t('login.email') }}</option>
                            <option v-for="({ type }) in documents" :value="type" :key="type">
                                {{ type }}
                            </option>
                        </select>
                    </div>
                    <base-input v-if="model.document_type === 'EMAIL'" v-model="model.email"
                        :name="$t('login.email')" class="nch-login__input" type="email" autocomplete="username"
                        :placeholder="$t('login.email')"></base-input>
                    <base-input v-else v-model="model.document" v-mask="documentMask" :name="$t('login.document')"
                        class="nch-login__input" :placeholder="model.document_type"
                        @blur="model.email = ''"></base-input>
                    <base-input :appendIcon="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"
                        v-model="model.password" :name="$t('login.password.title')" autocomplete="current-password"
                        :type="showPassword ? 'text' : 'password'" :placeholder="$t('login.password.title')"
                        class="nch-login__input" :clickAppendIcon="togglePasswordDisplay"></base-input>

                    <div class="nch-login__row">
                        <base-checkbox v-model="model.remember">{{ $t('login.remember') }}</base-checkbox>
                        <a href="#" class="nch-login__forgot" @click="forgotPassword($event)"
                            :title="$t('login.password.forgot')">{{ $t('login.password.forgot') }}</a>
                    </div>
                    <template v-if="disconnect">
                        <div class="alert alert-warning nch-login__device-alert" role="alert">
                            {{ $t('login.device_limit_hint') }}
                        </div>
                        <base-button type="warning" class="nch-login__disconnect w-100 mb-2"
                            :loading="loading" @click.prevent="disconnectAndLogin">
                            {{ $t('login.disconnect_and_signin') }}</base-button>
                    </template>

                    <div class="d-flex justify-content-center mt-3">
                        <Captcha v-if="modal && showCaptcha" ref="captcha"></Captcha>
                    </div>

                    <base-button native-type="submit" :loading="loading" type="primary"
                        class="nch-login__submit" :style="$bgColor(buttonColor)" :disabled="disabled">{{ $t('login.signin') }}</base-button>
                </template>
                <template v-else>
                    <div class="nch-login__field">
                        <label for="userselect">{{ $t('login.continue_user') }}</label>
                        <select id="userselect" name="user" class="form-control"
                            v-model="model.email" @input="model.document = null">
                            <option v-for="({ name, email }) in users" :value="email" :key="email">
                                {{ name }}
                            </option>
                        </select>
                    </div>
                    <div class="text-center">
                        <base-button native-type="button" class="my-4" @click.prevent="returnToLogin">
                            {{ $t('common.back') }}
                        </base-button>
                        <base-button native-type="submit" type="success" class="user-select-continue my-4"
                            :loading="loading" :disabled="disabled"
                            :style="[{ '--continue-progress': `${continueProgresss}%` }, $bgColor(buttonColor)]" @click="clearProgress">{{
                            $t('login.continue') }}</base-button>
                    </div>
                </template>
            </form>
        </div>
    </component>
</template>

<script>
import Modal from '../../argon/components/Modal';
import BaseButton from '../../argon/components/BaseButton';
import BaseInput from '../../argon/components/Inputs/BaseInput';
import BaseCheckbox from '../../argon/components/Inputs/BaseCheckbox';
import Captcha from '../Common/Captcha';

export default {
    name: 'Login',
    components: {
        Modal,
        BaseButton,
        BaseInput,
        BaseCheckbox,
        Captcha,
    },
    emits: ['update:modelValue', 'openRegister', 'forgotPassword'],
    props: {
        modelValue: {
            type: Boolean,
            required: true,
            description: 'Status da modal',
        },
        loginSocial: {
            type: Array,
            description: 'Redes sociais disponiveis para login',
        },
        hubHash: {
            type: String,
            description: 'Hash do hub',
        },
        page: {
            type: Boolean,
            description: 'Renderizado na pagina, sem modal',
        },
        color: {
            type: Object,
            default: () => ({}),
            description: 'Cores do tema (primary/secondary/button)',
        },
    },
    data() {
        return {
            loading: false,
            chosenSocial: '',
            disconnect: false,
            showPassword: false,
            model: {
                email: '',
                password: '',
                remember: false,
                disconnectDevices: false,
                document_type: 'EMAIL',
                document: '',
            },
            tries: 0,
            disabled: false,
            geolocationRequired: false,
            documents: [],
            users: null,
            continueProgresss: null,
            continueInterval: null,
        };
    },
    computed: {
        buttonColor() {
            return (this.color && (this.color.button || this.color.primary)) || '#5e72e4';
        },
        modal: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        selectUser() {
            return !!this.users;
        },
        documentMask() {
            const document = this.documents.find(({ type }) => this.model.document_type === type);
            const validMask = Array.from({ length: 60 }, () => 'X').join('');
            return (document && document.mask) || validMask;
        },
        redirect() {
            return this.$store.getters.REDIRECT_TO;
        },
        socials() {
            return this.$store.getters.SOCIALS;
        },
        filteredLogins() {
            let filteredLogin = [];
            if (this.socials && this.socials.length > 0) {
                return (filteredLogin = this.socials.filter(
                    item => item.enable,
                ));
            }
            if (this.loginSocial) {
                return (filteredLogin = this.loginSocial.filter(
                    item => item.enable,
                ));
            }
        },
        showCaptcha() {
            return this.tries > 1;
        },
        googleEnabled() {
            return (this.filteredLogins || []).some(
                ({ type }) => type === 'Google-plus',
            );
        },
        plan() {
            return this.$store.getters.SUBSCRIBE_PLAN;
        },
    },
    watch: {
        continueProgresss(progress) {
            if (progress === 100) {
                this.clearProgress();
                this.login();
            }
        },
    },
    beforeUnmount() {
        this.clearProgress();
    },
    mounted() {
        const params = new URLSearchParams(window.location.search);
        const redirectParam = params.get('redirect');
        if (redirectParam) {
            this.$store.commit('SET_REDIRECT', redirectParam);
        }
        this.checkGeolocation();
        this.getDocuments();
    },
    methods: {
        /**
         * Login desconectando os demais dispositivos (limite atingido) —
         * ação explícita no lugar do checkbox discreto (backlog#333)
         */
        disconnectAndLogin() {
            this.model.disconnectDevices = true;
            this.login();
        },

        login() {
            this.$validator.validate().then(async (valid) => {
                const captcha = this.showCaptcha
                    ? await this.$refs.captcha.send()
                    : true;
                if (valid && captcha) {
                    this.loading = true;
                    if (
                        this.geolocationRequired &&
                        (!this.model.lat || !this.model.lng)
                    ) {
                        this.loading = false;
                        this.requestGeolocation();
                        return this.$notify({
                            type: 'danger',
                            message: this.$t('common.geolocation_permission'),
                        });
                    }
                    const isDocumentLogin = this.model.document_type !== 'EMAIL' && !this.users;
                    window.axios
                        .post('/authenticate', {
                            ...this.model,
                            email: isDocumentLogin ? null : this.model.email,
                            document: isDocumentLogin ? this.model.document : null,
                        })
                        .then((res) => {
                            this.$notify({
                                type: 'success',
                                message: res.data.message,
                            });

                            if (res.data.token) {
                                localStorage.setItem(
                                    'nochalks_token',
                                    res.data.token,
                                );
                            }

                            if (this.plan) {
                                this.subscribe().then(() => {
                                    location.href = '/app/dashboard';
                                });
                            }

                            if (res.data.redirect) {
                                return (location.href = res.data.redirect);
                            }

                            if (this.redirect) {
                                localStorage.removeItem('redirect');
                                return (location.href = this.redirect);
                            }

                            setTimeout(() => {
                                location.href = '/app/dashboard';
                            }, 500);
                        })
                        .catch(({ response }) => {
                            this.loading = false;
                            const errors = response.data.errors;
                            let errorMessage = 'login.failed';
                            if (errors) {
                                errorMessage = this.handleErrorCode(errors);
                            }
                            if (errorMessage) {
                                this.$notify({
                                    type: 'danger',
                                    message: this.$t(errorMessage),
                                });
                            }
                        });
                }
            });
        },
        subscribe() {
            return window.axios
                .post(`api/cart/add/${this.plan.id}`)
                .then(res => res)
                .catch(err => cosole.log(err));
        },
        socialLogin(chosenSocial) {
            switch (chosenSocial) {
            case 'Google-plus':
                window.location.href = '/auth/google/redirect';
                break;
            default:
                console.error('Social login não suportado:', chosenSocial);
            }
        },
        async getDocuments() {
            try {
                const { data } = await window.axios.get('/api/app/documents');
                this.documents = data;
            } catch (e) {
                this.documents = [];
            }
        },
        returnToLogin() {
            this.users = null;
            this.clearProgress();
        },
        handleErrorCode(error) {
            try {
                const { code, message } = error;
                const [errorMessage] = message;
                const [errorCode] = code;

                const errorHandlers = {
                    auth: () => {
                        this.tries += 1;
                        return errorMessage;
                    },
                    device: () => {
                        this.tries += 1;
                        this.disconnect = true;
                        return errorMessage;
                    },
                    userselect: () => {
                        this.users = error.users;
                        const [selected] = this.users;
                        this.model.email = selected.email;
                        this.continueInterval = setInterval(() => {
                            if (this.continueProgresss < 100) {
                                this.continueProgresss += 1;
                            } else {
                                this.clearProgress();
                            }
                        }, 50);
                    },
                };
                this.users = null;
                const handler = errorHandlers[errorCode];
                return handler();
            } catch (e) {
                return 'login.messages.error';
            }
        },
        clearProgress() {
            if (this.continueInterval) {
                clearInterval(this.continueInterval);
            }
            this.progress = 0;
        },
        forgotPassword(e) {
            e.preventDefault();
            this.$emit('forgotPassword');
        },
        onVerify() {
            this.captcha = true;
            this.disabled = false;
        },
        onExpired() {
            this.$refs.recaptcha.reset();
        },
        checkGeolocation() {
            if (this.$getSettings.clientSettings.app && this.$getSettings.clientSettings.app.geographic) {
                this.requestGeolocation();
            }
        },
        requestGeolocation() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    this.model.lat = position.coords.latitude;
                    this.model.lng = position.coords.longitude;
                });
            } else {
                this.$notify({
                    type: 'danger',
                    message: this.$t('common.geolocation_unsupported'),
                });
            }
        },
        togglePasswordDisplay() {
            this.showPassword = !this.showPassword;
        },
    },
};
</script>

<style scoped>
.nch-login {
    background: #fff;
    border-radius: 16px;
    padding: 2rem 1.75rem;
    box-shadow: 0 24px 64px -16px rgba(15, 23, 42, 0.25);
}

.nch-login--page {
    width: 360px;
    box-shadow: 0 12px 40px -12px rgba(15, 23, 42, 0.18);
}

.nch-login__header {
    text-align: center;
    margin-bottom: 1.5rem;
}

.nch-login__title {
    font-size: 1.4rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin-bottom: 0.25rem;
}

.nch-login__subtitle {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;
}

.nch-login__subtitle a {
    font-weight: 600;
}

.nch-login__google {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.65rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #fff;
    color: #1e293b;
    font-weight: 600;
    font-size: 0.9rem;
    transition: background 0.15s ease, box-shadow 0.15s ease;
}

.nch-login__google:hover {
    background: #f8fafc;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.nch-login__divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.25rem 0 1rem;
    color: #94a3b8;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.nch-login__divider::before,
.nch-login__divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e2e8f0;
}

.nch-login__field {
    margin-bottom: 0.9rem;
}

.nch-login__field label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.25rem;
}

.nch-login__input {
    margin-bottom: 0.9rem;
}

.nch-login__input :deep(.form-control),
.nch-login__field .form-control {
    border-radius: 10px;
    border-color: #e2e8f0;
    padding: 0.65rem 0.9rem;
    height: auto;
}

.nch-login__input :deep(.input-group-text) {
    border-radius: 10px;
    border-color: #e2e8f0;
    cursor: pointer;
}

.nch-login__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
    font-size: 0.85rem;
}

.nch-login__forgot {
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
}

.nch-login__submit {
    width: 100%;
    margin-top: 1rem;
    border-radius: 10px;
    padding: 0.7rem;
    font-weight: 700;
}

.user-select-continue::after {
    transition: width ease-in .1ms;
}

.user-select-continue::after {
    position: absolute;
    top: 0;
    left: 0;
    width: var(--continue-progress);
    height: 100%;
    content: "";
    display: block;
    z-index: 29;
    background-color: rgba(255, 255, 255, 0.2);
}
</style>
